import { useEffect, useState } from 'react'
import { fetchPracticePlan } from '../data/api'
import type { PracticePlan } from '../types'
import { SectionLabel } from './RoundTimeline'

// The session, assembled. Everything here already existed across five tabs; what was missing was
// somebody putting it in one order and saying what to do about each line.
//
// The design rule this renders is "never look more confident than the data" — every item shows the
// number it came from so a coach can disagree with it, and a thin one says so rather than sitting
// alongside a solid one looking identical.

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const

const SOURCE: Record<string, string> = {
  repeated: 'said again and again',
  map: 'map record',
  timeout: 'in-game habit',
  economy: 'economy',
  ai: 'AI self-review',
}

export function Practice() {
  const [plan, setPlan] = useState<PracticePlan | null>(null)

  useEffect(() => {
    fetchPracticePlan().then(setPlan).catch(() => setPlan({ items: [], note: 'Couldn’t build a plan.' }))
  }, [])

  if (!plan) return <p className="text-sm text-text-dim">Building…</p>

  return (
    <section className="border border-border bg-surface p-5" style={card}>
      <SectionLabel n="T1" label="Next session" />
      {plan.note && <p className="mt-3 text-sm text-text-faint">{plan.note}</p>}
      {plan.items.length > 0 && (
        <>
          <ol className="mt-4 space-y-4">
            {plan.items.map((it, i) => (
              <li key={i} className="border-l-2 border-border pl-3">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-display text-sm text-text">{it.title}</span>
                  <span className="text-[10px] uppercase tracking-wider text-text-faint">
                    {SOURCE[it.source]}
                  </span>
                  {it.thin && (
                    <span className="text-[10px] text-loss" title="Real, but off a small sample">
                      thin data
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-text-dim">{it.evidence}</p>
                <p className="mt-1 text-xs leading-relaxed text-text-faint">{it.drill}</p>
              </li>
            ))}
          </ol>
          <p className="mt-4 text-[11px] leading-relaxed text-text-faint">
            Ordered by what a session should open with, and deliberately short — a plan padded to a
            fixed length invents its tail, and the invented part is what gets the whole thing
            ignored. Every line names the number behind it so you can disagree with it.
          </p>
        </>
      )}
    </section>
  )
}
